import { CartItems, VatRates } from './cart';
import { Backend } from '.';

export enum OrderProcessStatus {
  unapproved = 'unapproved',
  new = 'new',
  pending = 'pending',
  processing = 'processing',
  done = 'done',
  failure = 'failure',
  hold = 'hold',
}

export interface PaymentMethodInfo {
  account_owner: string;
  bank_name: string;
  bic: string;
  iban: string;
  reference: string;
  amount: number;
}
/**
 * - PrePersonalization: When you need to fill out the form before a hard-ticket can be printed and sent
 * - Voucher: an online printable ticket
 */
export type VoucherPersonalizationType = 'PrePersonalization' | 'Voucher';

export interface VoucherPersonalization {
  id: number;
  type: VoucherPersonalizationType;
  variant_id?: number;
  variant_tile?: string;
  personalized?: boolean;
  firstname: string;
  lastname: string;
  phone: string;
  email: string;
  street: string;
  zip_code: string;
  city: string;
  country: string;
  valid?: boolean;
}

export interface Personalization {
  /**
   * if true: let user personalize tickets
   * if false: can't be personalized anymore
   */
  all_done?: boolean;
  would_change_to_online?: boolean;
  can_be_changed: boolean; // not in use
  personalizations: VoucherPersonalization[];
}

export interface Coupon {
  id: number;
  value: string;
  value_words: string;
  token: string;
  // rel path to downloadable pdf
  download: string;
}

export interface OrderPreview {
  cancelled_at: Date;
  created_at: Date;
  id: number;
  order_no: string;
  ordered_at: Date;
  paid_at: Date;
  payment_status: OrderProcessStatus;
  returned_at: Date;
  shipped_at: Date;
  shipping_status: OrderProcessStatus;
  simple_token?: string;
  voucher_file?: string;
  vouchers: Voucher[];
}

export interface Voucher {
  id: number;
  pkpass_file?: string;
  variant: string;
  cancelled?: boolean;
}

export interface OrderDetail extends OrderPreview {
  cart_token?: string;
  items: CartItems;
  value: number;
  balance: number;
  vat_rates: VatRates;
  payment_method: string;
  payment_method_sid: string;
  payment_method_info?: PaymentMethodInfo;
  payment_action: string;
  shipping_method: string;
  address_addition?: string;
  city: string;
  company?: string;
  country: string;
  firstname: string;
  lastname: string;
  state?: string;
  street: string;
  zip_code: string;
  email?: string;
  phone?: string;
  use_shipping_address: boolean;
  shipping_address_addition?: string;
  shipping_city: string;
  shipping_companyname?: string;
  shipping_country: string;
  shipping_firstname: string;
  shipping_lastname: string;
  shipping_state?: string;
  shipping_street: string;
  shipping_zip_code: string;
  /**
   * Tells if there is already a registered online customer behind this order, if true then there is no customer yet
   */
  registerable: boolean;
  customer_registered: boolean;
  needs_online_payment: boolean;
  /**
   * COVID-19 (and possibly other) personalization information
   */
  personalization?: Personalization;
  /**
   * if show was cancelled personalization can be skipped
   */
  cancelled?: boolean;
  /**
   * If supplied the order will change from for example "hardticket" to "online"
   */
  change_to_online?: true;
  /**
   * Wenn man einen neuen Swap anlegen kann/darf, existiert ein new_voucher_swap_path.
   * Wenn der gesetzt ist, sollte auf der Bestellungs-Detailseite ein Link auftauchen, der auf diese URL zeigt.
   */
  new_voucher_swap_path?: string;

  bought_coupons?: Coupon[];
}

export type Orders = OrderPreview[];

export interface IndexedOrders {
  [orderId: number]: OrderDetail;
}

export async function getOrder(orderId: number, xtoken?: string) {
  if (!xtoken) return Backend.get<OrderDetail>(`/orders/${encodeURIComponent(orderId)}`);
  return Backend.get<OrderDetail>(`/orders/${encodeURIComponent(orderId)}?xtoken=${encodeURIComponent(xtoken)}`);
}

export async function patchOrder(order: Partial<OrderDetail>, orderId: number, xtoken?: string) {
  // if IE is dead (~September 2021) use `new URLSearchParams`
  const qs = xtoken ? `?xtoken=${encodeURIComponent(xtoken)}` : '';
  return Backend.patch<OrderDetail>(`/orders/${encodeURIComponent(orderId)}${qs}`, {
    body: JSON.stringify(order),
  });
}

export async function getOrders() {
  return Backend.get<Orders>(`/orders`);
}
